<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import PdvProductList from './PdvProductList.vue';
import PdvCheckout from './PdvCheckout.vue';
import PdvSettingsPanel from './PdvSettingsPanel.vue';
import AtecCheckout from './AtecCheckout.vue';
import InovafarmaLogo from 'dashboard/assets/images/inovafarma.svg';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
  inboxId: {
    type: [String, Number],
    required: true,
  },
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const currentAccountId = useMapGetter('getCurrentAccountId');
const { isAdmin } = useAdmin();

// eslint-disable-next-line no-unused-vars
const isInovaFarmaEnabled = computed(() => {
  return store.getters['accounts/isInovaFarmaEnabled'](currentAccountId.value);
});

const pdvIntegrationProvider = computed(() =>
  store.getters['accounts/pdvIntegrationProvider'](currentAccountId.value)
);

// eslint-disable-next-line no-unused-vars
const isAtecEnabled = computed(() =>
  store.getters['accounts/isAtecEnabled'](currentAccountId.value)
);
const isPdvEnabled = computed(() => {
  const pdvFlag = window.globalConfig?.ENABLE_PDV;
  return pdvFlag !== false && pdvFlag !== 'false';
});

// Sistema de carrinho por conversa
const CART_STORAGE_KEY = 'pdv_carts_by_conversation';
const carts = ref(LocalStorage.get(CART_STORAGE_KEY) || {});

// Estados do carrinho: 'closed' | 'floating'
const cartState = ref('closed');

// Carrinho da conversa atual (computed baseado no conversationId)
const cart = computed({
  get: () => carts.value[props.conversationId] || [],
  set: newCart => {
    carts.value[props.conversationId] = newCart;
    // Salvar no localStorage
    LocalStorage.set(CART_STORAGE_KEY, carts.value);
  },
});

// Watch para resetar estado do painel ao trocar de conversa
watch(
  () => props.conversationId,
  (newId, oldId) => {
    if (newId !== oldId) {
      // Fechar painel ao trocar de conversa
      cartState.value = 'closed';

      // Garantir que o carrinho da nova conversa existe
      if (!carts.value[newId]) {
        carts.value[newId] = [];
      }
    }
  },
  { immediate: true }
);

watch(
  () => cart.value,
  newCart => {
    emitter.emit(BUS_EVENTS.PDV_CART_UPDATED, {
      cart: (newCart || []).map(item => ({ ...item })),
      conversationId: props.conversationId,
      inboxId: props.inboxId,
      contactId: props.contactId,
    });
  },
  { deep: true }
);

const activePanel = ref('products');
const activeTabName = computed(() => {
  return activePanel.value === 'settings'
    ? 'PDV.SETTINGS.TITLE'
    : 'PDV.PRODUCTS';
});

const openSettings = () => {
  activePanel.value = 'settings';
};

const openProducts = () => {
  activePanel.value = 'products';
};

const toggleCart = () => {
  cartState.value = cartState.value === 'closed' ? 'floating' : 'closed';
};

const closeCart = () => {
  cartState.value = 'closed';
};

const addToCart = product => {
  const currentCart = cart.value;
  const existing = currentCart.find(item => item.id === product.id);
  if (existing) {
    existing.quantity += 1;
  } else {
    const firstPrice = product.availablePrices?.[0]?.price ?? product.price;
    currentCart.push({
      ...product,
      quantity: 1,
      customPrice: firstPrice,
      itemDiscount: 0,
    });
  }
  cart.value = [...currentCart];
};

const allowProductDiscountPerItem = computed(() => {
  return store.getters['accounts/allowProductDiscountPerItem'](
    currentAccountId.value
  );
});

const maxDiscountPercent = computed(() => {
  return store.getters['accounts/maxDiscountPercent'](currentAccountId.value);
});

watch(
  allowProductDiscountPerItem,
  isEnabled => {
    if (isEnabled) return;

    const sanitizedCart = cart.value.map(item => ({
      ...item,
      customPrice: item.price,
    }));
    cart.value = sanitizedCart;
  },
  { immediate: true }
);

const removeFromCart = productId => {
  cart.value = cart.value.filter(item => item.id !== productId);
};

const updateQuantity = (productId, quantity) => {
  const currentCart = cart.value;
  const item = currentCart.find(cartItem => cartItem.id === productId);
  if (item) {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      item.quantity = quantity;
      cart.value = [...currentCart];
    }
  }
};

const updateItemPrice = (productId, newPrice) => {
  const currentCart = cart.value;
  const item = currentCart.find(cartItem => cartItem.id === productId);
  if (item) {
    item.customPrice = Math.max(0, newPrice);
    cart.value = [...currentCart];
  }
};

const clearCart = () => {
  cart.value = [];
};

const cartItemsCount = computed(() => {
  return cart.value.reduce((total, item) => total + item.quantity, 0);
});

const cartTotal = computed(() => {
  return cart.value.reduce(
    (total, item) => total + (item.customPrice ?? item.price) * item.quantity,
    0
  );
});

const { updateUISettings } = useUISettings();

const closePdvPanel = () => {
  updateUISettings({ is_pdv_panel_open: false });
};

const contactSupport = () => {
  window.open(
    'mailto:suporte@geniuscloud.com.br?subject=Integração Inova Farma',
    '_blank'
  );
};

const formatPrice = price => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
};

// Posicionamento e redimensionamento do carrinho
const CART_POSITION_KEY = 'pdv_cart_position';
const CART_SIZE_KEY = 'pdv_cart_size';
const cartPanelRef = ref(null);
const isDragging = ref(false);
const isResizing = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

const MIN_W = 320;
const MIN_H = 360;
const DEFAULT_W = 1040;

const cartPosition = ref({ left: 0, top: 0 });
const panelSize = ref({ width: DEFAULT_W, height: 0 });

onMounted(() => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const savedSize = LocalStorage.get(CART_SIZE_KEY);
  const w = Math.max(MIN_W, Math.min(savedSize?.width || DEFAULT_W, vw * 0.95));
  const h = Math.max(MIN_H, Math.min(savedSize?.height || vh * 0.7, vh * 0.85));
  panelSize.value = { width: w, height: h };

  const savedPos = LocalStorage.get(CART_POSITION_KEY);
  if (savedPos?.left !== undefined) {
    cartPosition.value = {
      left: Math.max(16, Math.min(vw - w - 16, savedPos.left)),
      top: Math.max(16, Math.min(vh - h - 16, savedPos.top)),
    };
  } else if (savedPos?.right !== undefined) {
    // Migrar do formato antigo right/bottom
    cartPosition.value = {
      left: Math.max(16, vw - (savedPos.right || 24) - w),
      top: Math.max(16, vh - (savedPos.bottom || 24) - h),
    };
  } else {
    cartPosition.value = { left: vw - w - 24, top: vh - h - 24 };
  }
});

const startDrag = event => {
  if (event.target.closest('button') || event.target.closest('.no-drag'))
    return;

  isDragging.value = true;
  const rect = cartPanelRef.value.getBoundingClientRect();
  dragOffset.value = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = event => {
  if (!isDragging.value) return;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { width: w, height: h } = panelSize.value;

  cartPosition.value = {
    left: Math.max(
      16,
      Math.min(vw - w - 16, event.clientX - dragOffset.value.x)
    ),
    top: Math.max(
      16,
      Math.min(vh - h - 16, event.clientY - dragOffset.value.y)
    ),
  };
};

const stopDrag = () => {
  if (!isDragging.value) return;
  isDragging.value = false;
  LocalStorage.set(CART_POSITION_KEY, cartPosition.value);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

let resizeState = null;

const startResize = (direction, event) => {
  event.preventDefault();
  event.stopPropagation();
  isResizing.value = true;

  resizeState = {
    direction,
    startX: event.clientX,
    startY: event.clientY,
    startLeft: cartPosition.value.left,
    startTop: cartPosition.value.top,
    startWidth: panelSize.value.width,
    startHeight: panelSize.value.height,
  };

  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', stopResize);
};

const onResize = event => {
  if (!isResizing.value || !resizeState) return;

  const {
    direction,
    startX,
    startY,
    startLeft,
    startTop,
    startWidth,
    startHeight,
  } = resizeState;
  const dx = event.clientX - startX;
  const dy = event.clientY - startY;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = startLeft;
  let top = startTop;
  let width = startWidth;
  let height = startHeight;

  if (direction.includes('e')) width = startWidth + dx;
  if (direction.includes('w')) {
    width = startWidth - dx;
    left = startLeft + dx;
  }
  if (direction.includes('s')) height = startHeight + dy;
  if (direction.includes('n')) {
    height = startHeight - dy;
    top = startTop + dy;
  }

  width = Math.max(MIN_W, Math.min(vw * 0.95, width));
  height = Math.max(MIN_H, Math.min(vh * 0.85, height));
  left = Math.max(16, Math.min(vw - width - 16, left));
  top = Math.max(16, Math.min(vh - height - 16, top));

  cartPosition.value = { left, top };
  panelSize.value = { width, height };
};

const stopResize = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  resizeState = null;
  LocalStorage.set(CART_POSITION_KEY, cartPosition.value);
  LocalStorage.set(CART_SIZE_KEY, panelSize.value);
  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
};

const cartPanelStyle = computed(() => ({
  left: `${cartPosition.value.left}px`,
  top: `${cartPosition.value.top}px`,
  width: `${panelSize.value.width}px`,
  height: `${panelSize.value.height}px`,
}));
</script>

<template>
  <div class="relative flex flex-col h-full w-full bg-n-solid-1">
    <!-- Header -->
    <div
      class="flex items-center justify-between p-4 border-b border-n-weak bg-n-solid-1 z-20"
    >
      <div class="flex items-center gap-3">
        <img
          v-if="pdvIntegrationProvider !== 'atec'"
          :src="InovafarmaLogo"
          alt="Inovafarma"
          class="h-16 w-auto"
        />
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span v-else class="text-lg font-bold text-n-brand tracking-wide">
          ATEC Gestão
        </span>
        <h2 class="text-xl font-bold text-n-text">{{ $t(activeTabName) }}</h2>
      </div>
      <div class="flex items-center gap-1">
        <Button
          v-if="activePanel === 'products' && isAdmin"
          v-tooltip="$t('PDV.SETTINGS.TITLE')"
          icon="i-lucide-settings"
          ghost
          sm
          @click="openSettings"
        />
        <Button
          v-else
          v-tooltip="$t('PDV.SETTINGS.BACK_TO_PRODUCTS')"
          icon="i-lucide-arrow-left"
          ghost
          sm
          @click="openProducts"
        />
        <Button
          v-tooltip="$t('GENERAL.CLOSE')"
          icon="i-lucide-x"
          ghost
          sm
          @click="closePdvPanel"
        />
      </div>
    </div>

    <!-- Conteúdo com blur quando desabilitado -->
    <div
      class="flex-1 overflow-hidden relative flex flex-col"
      :class="{
        'blur-sm pointer-events-none': !isPdvEnabled || !pdvIntegrationProvider,
      }"
    >
      <!-- Lista de Produtos (ocupa tudo) -->
      <div
        v-if="activePanel === 'products'"
        class="flex-1 overflow-hidden flex flex-col"
      >
        <PdvProductList
          :integration="pdvIntegrationProvider || 'inovafarma'"
          @add-to-cart="addToCart"
        />
      </div>
      <PdvSettingsPanel v-else-if="isAdmin" @back-to-products="openProducts" />

      <!-- Botão Flutuante do Carrinho (Badge) -->
      <button
        v-if="cart.length > 0"
        class="fixed bottom-6 right-6 z-30 group"
        :class="{
          'opacity-50': cartState === 'floating',
        }"
        @click="toggleCart"
      >
        <div class="relative">
          <!-- Badge principal -->
          <div
            class="flex items-center gap-3 px-5 py-3.5 bg-gradient-to-r from-n-brand to-n-brand/80 hover:from-n-brand hover:to-n-brand text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:scale-105"
          >
            <span class="i-ph-shopping-cart-bold text-2xl" />
            <div class="flex flex-col items-start">
              <span class="text-xs font-medium opacity-90">Carrinho</span>
              <span class="text-base font-bold">{{
                formatPrice(cartTotal)
              }}</span>
            </div>
            <!-- Contador de itens -->
            <span
              class="px-2.5 py-1 bg-white text-n-brand text-sm font-bold rounded-full"
            >
              {{ cartItemsCount }}
            </span>
          </div>

          <!-- Pulse animation quando carrinho está fechado -->
          <span
            v-if="cartState === 'closed'"
            class="absolute inset-0 rounded-full bg-n-brand opacity-75 animate-ping"
          />
        </div>
      </button>

      <!-- Painel Flutuante do Carrinho -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        leave-active-class="transition-all duration-200 ease-in"
        enter-from-class="scale-95 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-95 opacity-0"
      >
        <div
          v-if="cartState === 'floating' && cart.length > 0"
          ref="cartPanelRef"
          :style="cartPanelStyle"
          class="fixed z-40 bg-white dark:bg-n-solid-1 rounded-2xl shadow-2xl border border-n-weak flex flex-col overflow-hidden"
          :class="{ 'select-none': isDragging || isResizing }"
        >
          <!-- Handles de resize em todas as bordas e cantos -->
          <div
            class="absolute top-0 left-4 right-4 h-1.5 cursor-n-resize z-50"
            @mousedown.stop="startResize('n', $event)"
          />
          <div
            class="absolute bottom-0 left-4 right-4 h-1.5 cursor-s-resize z-50"
            @mousedown.stop="startResize('s', $event)"
          />
          <div
            class="absolute top-4 bottom-4 right-0 w-1.5 cursor-e-resize z-50"
            @mousedown.stop="startResize('e', $event)"
          />
          <div
            class="absolute top-4 bottom-4 left-0 w-1.5 cursor-w-resize z-50"
            @mousedown.stop="startResize('w', $event)"
          />
          <div
            class="absolute top-0 right-0 w-4 h-4 cursor-ne-resize z-50"
            @mousedown.stop="startResize('ne', $event)"
          />
          <div
            class="absolute top-0 left-0 w-4 h-4 cursor-nw-resize z-50"
            @mousedown.stop="startResize('nw', $event)"
          />
          <div
            class="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize z-50"
            @mousedown.stop="startResize('se', $event)"
          />
          <div
            class="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize z-50"
            @mousedown.stop="startResize('sw', $event)"
          />

          <!-- Header do carrinho flutuante (área de drag) -->
          <div
            class="cart-drag-handle flex items-center justify-between px-5 py-4 border-b border-n-weak bg-gradient-to-r from-n-brand/5 to-transparent cursor-grab active:cursor-grabbing"
            @mousedown="startDrag"
          >
            <div class="flex items-center gap-3">
              <span
                class="i-lucide-grip-vertical text-lg text-n-text-subtle/50"
              />
              <span class="i-ph-shopping-cart-bold text-2xl text-n-brand" />
              <div>
                <h3 class="font-bold text-base text-n-text">
                  Carrinho de Vendas
                </h3>
                <p class="text-xs text-n-text-subtle">
                  {{ cartItemsCount }}
                  {{ cartItemsCount === 1 ? 'item' : 'itens' }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 no-drag">
              <Button
                v-tooltip="'Fechar'"
                icon="i-lucide-x"
                ghost
                sm
                @click="closeCart"
              />
            </div>
          </div>

          <!-- Conteúdo do carrinho (scroll independente) -->
          <div class="flex-1 overflow-y-auto">
            <AtecCheckout
              v-if="pdvIntegrationProvider === 'atec'"
              :key="`atec-checkout-${conversationId}-${contactId}`"
              :cart="cart"
              :conversation-id="conversationId"
              :inbox-id="inboxId"
              :contact-id="contactId"
              :allow-product-discount-per-item="allowProductDiscountPerItem"
              :max-discount-percent="maxDiscountPercent"
              @update-quantity="updateQuantity"
              @update-item-price="updateItemPrice"
              @remove-from-cart="removeFromCart"
              @clear-cart="clearCart"
            />
            <PdvCheckout
              v-else
              :key="`pdv-checkout-${conversationId}-${contactId}`"
              :cart="cart"
              :conversation-id="conversationId"
              :inbox-id="inboxId"
              :contact-id="contactId"
              :allow-product-discount-per-item="allowProductDiscountPerItem"
              :max-discount-percent="maxDiscountPercent"
              @update-quantity="updateQuantity"
              @update-item-price="updateItemPrice"
              @remove-from-cart="removeFromCart"
              @clear-cart="clearCart"
            />
          </div>
        </div>
      </transition>

      <!-- Backdrop escuro (apenas visual, não fecha ao clicar) -->
      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="cartState === 'floating'"
          class="fixed inset-0 bg-black/10 dark:bg-black/20 z-35 pointer-events-none"
        />
      </transition>
    </div>

    <!-- Tela de bloqueio quando PDV está desativado -->
    <div
      v-if="!isPdvEnabled"
      class="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-10"
    >
      <div class="max-w-md mx-4 text-center">
        <div class="mb-6 flex justify-center">
          <img
            :src="InovafarmaLogo"
            alt="Inovafarma"
            class="h-24 w-auto opacity-50"
          />
        </div>
        <h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-slate-100">
          {{ $t('PDV.BLOCKED.TITLE') }}
        </h2>
        <p class="text-base text-slate-600 dark:text-slate-400 mb-6">
          {{ $t('PDV.BLOCKED.DESCRIPTION') }}
        </p>
        <div
          class="bg-woot-50 dark:bg-woot-900/20 border-2 border-woot-200 dark:border-woot-800 rounded-lg p-4"
        >
          <p class="text-sm text-slate-700 dark:text-slate-300">
            {{ $t('PDV.BLOCKED.SUBTEXT') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Tela de bloqueio quando nenhuma integração PDV está ativada -->
    <div
      v-else-if="!pdvIntegrationProvider"
      class="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-10"
    >
      <div class="max-w-md mx-4 text-center">
        <h2 class="text-2xl font-bold mb-4 text-slate-900 dark:text-slate-100">
          {{ $t('PDV.INTEGRATION_NOT_ENABLED.TITLE') }}
        </h2>
        <p class="text-base text-slate-600 dark:text-slate-400 mb-6">
          {{ $t('PDV.INTEGRATION_NOT_ENABLED.DESCRIPTION') }}
        </p>
        <Button
          variant="solid"
          woot
          size="large"
          icon="i-lucide-mail"
          class="w-full"
          @click="contactSupport"
        >
          {{ $t('PDV.INTEGRATION_NOT_ENABLED.CONTACT_SUPPORT') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Estilo do header arrastável */
.cart-drag-handle {
  user-select: none;
}

.cart-drag-handle:hover {
  background: linear-gradient(
    to right,
    rgb(var(--color-woot-50) / 0.08),
    transparent
  );
}

.cart-drag-handle:active {
  cursor: grabbing !important;
}
</style>
